/**
 * Klaviyo "Product Viewed" metric (TWS-1379).
 *
 * One event per variant viewed: once on PDP load, then again whenever the shopper changes variant.
 * Every property is resolved in Liquid by `snippets/klaviyo-product-viewed-data.liquid`, which lives
 * inside the product section - so the payload the variant picker re-fetches is already correct for
 * the new variant and this file never has to recompute a price, title or sales channel.
 *
 * NOT the same metric as Klaviyo's native Shopify "Viewed Product". The RA browse-abandon flow must
 * trigger on "Product Viewed" or it will read the integration's metric instead.
 *
 * Sending (identify vs guest anonymous_id) lives in @theme/metric-transport, shared with the Search
 * Query metric. Do not rename this file to klaviyo-events.js; that name is in the
 * perf-defer-script blocklist and a parked script never runs.
 */
import { ThemeEvents } from '@theme/events';
import { trackKlaviyoEvent } from '@theme/metric-transport';

const METRIC_NAME = 'Product Viewed';
const PAYLOAD_SELECTOR = 'script[type="application/json"][data-klaviyo-product-viewed]';

/**
 * Klaviyo's company id is read from its onsite script, so a send that beats that script would fall
 * back to the cookie queue — and for a guest that means filing on whatever profile a previous
 * `identify()` left behind (the TWS-1378 bug). Wait briefly for it, then send regardless.
 */
const KLAVIYO_READY_TIMEOUT_MS = 5000;
const KLAVIYO_POLL_MS = 200;

/**
 * @typedef {Record<string, string | number | boolean>} ProductViewedPayload
 */

/** Variant ids already sent on this page view. */
const sentVariantIds = new Set();

/** @type {ProductViewedPayload[]} */
const pending = [];
let waitingForKlaviyo = false;

/** Product this page is tracking. Changes only on a combined-listing variant update. */
let trackedProductId = '';

/**
 * @returns {boolean}
 */
function klaviyoPresent() {
  if (window.klaviyo) return true;

  for (const script of document.scripts) {
    if (/klaviyo\.com\/onsite\/js\//.test(script.src || '')) return true;
  }

  return false;
}

/**
 * @param {() => void} run
 */
function whenKlaviyoReady(run) {
  if (klaviyoPresent()) {
    run();
    return;
  }

  const startedAt = Date.now();
  const timer = window.setInterval(() => {
    if (!klaviyoPresent() && Date.now() - startedAt < KLAVIYO_READY_TIMEOUT_MS) return;
    window.clearInterval(timer);
    run();
  }, KLAVIYO_POLL_MS);
}

/**
 * FIFO so a variant switched during the readiness wait cannot be reported before the variant that
 * was actually viewed first.
 * @param {ProductViewedPayload} payload
 */
function queueSend(payload) {
  const variantId = String(payload.variant_id || '');
  if (!variantId || sentVariantIds.has(variantId)) return;

  sentVariantIds.add(variantId);
  pending.push(payload);

  if (waitingForKlaviyo) return;
  waitingForKlaviyo = true;

  whenKlaviyoReady(() => {
    waitingForKlaviyo = false;
    while (pending.length) {
      const next = pending.shift();
      if (next) trackKlaviyoEvent(METRIC_NAME, eventProperties(next));
    }
  });
}

/**
 * `skip` is ours, not Klaviyo's — drop it so it never reaches the metric.
 * @param {ProductViewedPayload} payload
 * @returns {Record<string, string | number>}
 */
function eventProperties(payload) {
  const { skip, ...properties } = payload;
  return /** @type {Record<string, string | number>} */ (properties);
}

/**
 * @param {ParentNode} root
 * @param {string} [productId]
 * @returns {ProductViewedPayload | null}
 */
function readPayload(root, productId) {
  const scoped = productId
    ? root.querySelector(`${PAYLOAD_SELECTOR}[data-product-id="${productId}"]`)
    : root.querySelector(PAYLOAD_SELECTOR);
  if (!scoped?.textContent) return null;

  try {
    const parsed = JSON.parse(scoped.textContent);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * @param {ProductViewedPayload | null} payload
 */
function sendIfViewable(payload) {
  if (!payload || payload.skip === true) return;
  queueSend(payload);
}

/**
 * The authoritative payload is the one in the freshly fetched section HTML, exactly as
 * `product-delivery-cutoff.js` reconciles against it.
 *
 * Scoping to `trackedProductId` is what keeps a quick-add / add-to-bag dialog for some other
 * product (a Rebuy slider on the PDP, say) from reporting itself as the product being viewed.
 * @param {Event} event
 */
function handleVariantUpdate(event) {
  const detail = /** @type {{ detail?: { data?: { html?: Document, newProduct?: { id: string } } } }} */ (event).detail;
  const doc = detail?.data?.html;
  if (!doc || typeof doc.querySelector !== 'function') return;

  const newProduct = detail?.data?.newProduct;
  if (newProduct?.id) trackedProductId = String(newProduct.id);

  sendIfViewable(readPayload(doc, trackedProductId));
}

function init() {
  if (window.Shopify?.designMode) return;

  const payload = readPayload(document);
  if (!payload) return;

  trackedProductId = String(payload.product_id || '');
  sendIfViewable(payload);

  document.addEventListener(ThemeEvents.variantUpdate, handleVariantUpdate);
}

init();
